import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { safeFormatDate, safeFormatUTCDateTime } from '../../utils/dateUtils';
import StatusDropdownButton from './StatusDropdownButton';

const GIFollowupModal = ({ isOpen, onClose, survey, fetchWithAuth, currentUser, onFollowupAdded }) => {
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingFollowupId, setDeletingFollowupId] = useState(null);
  const textareaRef = React.useRef(null);

  // Calculer le statut actuel (dernier followup)
  const currentStatus = React.useMemo(() => {
    if (followups.length === 0) return null;
    const sortedFollowups = [...followups].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return sortedFollowups[0]?.status || null;
  }, [followups]);

  // Vérifier si on peut ajouter une note
  const canAddNote = currentStatus !== 'done' && currentStatus !== 'wontdo';

  // Gestion des raccourcis clavier et auto-focus
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;

      // Fermer avec Échap
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      // Soumettre avec Cmd+Entrée (Mac) ou Ctrl+Entrée (PC)
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        if (canAddNote && newNote.trim() && !isSubmitting) {
          handleSubmitFollowup('pending');
        }
        return;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);

      // Auto-focus sur la textarea si elle est disponible
      if (canAddNote && textareaRef.current) {
        // Petit délai pour s'assurer que la modale est complètement rendue
        setTimeout(() => {
          textareaRef.current?.focus();
        }, 100);
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, canAddNote, newNote, isSubmitting]);

  // Fetch des followups existants
  useEffect(() => {
    const fetchFollowups = async () => {
      if (!isOpen || !survey?.MissionDayId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/gi/followups?missionDayId=${survey.MissionDayId}&promoterId=${survey.PromoterId}`
        );

        if (response.ok) {
          const data = await response.json();
          setFollowups(data.followups || []);
        } else {
          setError('Erreur lors du chargement des followups');
          console.error("Failed to fetch followups");
        }
      } catch (error) {
        setError('Erreur lors du chargement des followups');
        console.error("Error fetching followups:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFollowups();
  }, [isOpen, survey?.MissionDayId, fetchWithAuth]);

  // Supprimer un followup
  const handleDeleteFollowup = async (followupId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette relance ?')) {
      return;
    }

    setDeletingFollowupId(followupId);
    setError(null);

    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/dwh/gi/followup/${followupId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.ok) {
        // Retirer le followup de la liste
        setFollowups(prev => prev.filter(f => f.id !== followupId));
        if (onFollowupAdded) {
          // Décrémenter le count dans la liste principale
          onFollowupAdded(survey.MissionDayId, -1);
        }
      } else {
        setError('Erreur lors de la suppression du followup');
        console.error("Failed to delete followup");
      }
    } catch (error) {
      setError('Erreur lors de la suppression du followup');
      console.error("Error deleting followup:", error);
    } finally {
      setDeletingFollowupId(null);
    }
  };

  // Ajouter un nouveau followup
  const handleSubmitFollowup = async (status = 'pending', presetMessage = null) => {
    const noteToSubmit = presetMessage || newNote.trim();
    if (!noteToSubmit) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/dwh/gi/followup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            missionDayId: survey.MissionDayId,
            promoterId: survey.PromoterId,
            userId: currentUser.id,
            notes: noteToSubmit,
            status: status
          })
        }
      );

            if (response.ok) {
        const data = await response.json();
        console.log('API Response:', data); // Debug pour voir la structure de la réponse

        // Gérer le fait que followup peut être un array ou un objet
        const followupData = Array.isArray(data.followup) ? data.followup[0] : data.followup;

        // Enrichir le nouveau followup avec les infos d'affichage
        const enrichedFollowup = {
          ...followupData,
          id: followupData?.id,
          username: 'vous',
          created_at: new Date().toISOString(),
          user_id: currentUser.id,
          notes: noteToSubmit,
          status: status
        };

        console.log('Enriched followup:', enrichedFollowup); // Debug pour vérifier l'ID
        setFollowups(prev => [enrichedFollowup, ...prev]);
        setNewNote('');
        if (onFollowupAdded) {
          onFollowupAdded(survey.MissionDayId, 1, status);
        }
      } else {
        setError('Erreur lors de l\'ajout du followup');
        console.error("Failed to add followup");
      }
    } catch (error) {
      setError('Erreur lors de l\'ajout du followup');
      console.error("Error adding followup:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction pour afficher le statut
  const getStatusDisplay = (status) => {
    const statusConfig = {
      pending: {
        label: 'En attente',
        icon: '⏳',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/20',
        textColor: 'text-yellow-800 dark:text-yellow-200',
        borderColor: 'border-yellow-200 dark:border-yellow-800'
      },
      done: {
        label: 'Terminé',
        icon: '✅',
        bgColor: 'bg-green-100 dark:bg-green-900/20',
        textColor: 'text-green-800 dark:text-green-200',
        borderColor: 'border-green-200 dark:border-green-800'
      },
      wontdo: {
        label: 'Abandonné',
        icon: '❌',
        bgColor: 'bg-red-100 dark:bg-red-900/20',
        textColor: 'text-red-800 dark:text-red-200',
        borderColor: 'border-red-200 dark:border-red-800'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config.bgColor} ${config.textColor} ${config.borderColor}`}>
        <span className="mr-1">{config.icon}</span>
        {config.label}
      </span>
    );
  };

  // Fonction pour obtenir le label du statut
  const getStatusLabel = (status) => {
    const statusLabels = {
      pending: 'En attente',
      done: 'Terminé',
      wontdo: 'Abandonné'
    };

    return statusLabels[status] || status;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                Suivi des relances
              </h3>
              <button
                onClick={onClose}
                className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <span className="sr-only">Fermer</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Informations du survey */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Promoteur:</span>
                  <span className="ml-2 text-gray-900 dark:text-gray-100">
                    {`${survey.PromoterFirstName || ''} ${survey.PromoterLastName || ''}`.trim()}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Date mission:</span>
                  <span className="ml-2 text-gray-900 dark:text-gray-100">
                    {safeFormatDate(survey.MissionDayDate)}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Campagne:</span>
                  <span className="ml-2 text-gray-900 dark:text-gray-100">
                    {survey.CampaignName}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Point d'intervention:</span>
                  <span className="ml-2 text-gray-900 dark:text-gray-100">
                    {survey.InterventionPointName}
                  </span>
                </div>
              </div>
            </div>

            {/* Formulaire d'ajout de followup */}
            {canAddNote ? (
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">
                  Ajouter une relance
                </h4>

                {/* Messages pré-enregistrés */}
                <div className="mb-4">
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Messages rapides
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {[
                      {
                        icon: '📞',
                        message: 'Ne répond pas, message laissé sur répondeur',
                        status: 'pending',
                        color: 'border-yellow-200 hover:bg-yellow-50 dark:border-yellow-800 dark:hover:bg-yellow-900/20'
                      },
                      {
                        icon: '👍',
                        message: 'A répondu, va le faire dans la journée',
                        status: 'pending',
                        color: 'border-yellow-200 hover:bg-yellow-50 dark:border-yellow-800 dark:hover:bg-yellow-900/20'
                      },
                      {
                        icon: '📵',
                        message: 'Impossible à joindre, impossible de laisser un message',
                        status: 'pending',
                        color: 'border-yellow-200 hover:bg-yellow-50 dark:border-yellow-800 dark:hover:bg-yellow-900/20'
                      },
                      {
                        icon: '🎯',
                        message: 'Fait ensemble par téléphone',
                        status: 'done',
                        color: 'border-green-200 hover:bg-green-50 dark:border-green-800 dark:hover:bg-green-900/20'
                      },
                      {
                        icon: '✅',
                        message: 'CR déjà rempli',
                        status: 'done',
                        color: 'border-green-200 hover:bg-green-50 dark:border-green-800 dark:hover:bg-green-900/20'
                      },
                      {
                        icon: '🧩',
                        message: 'OK sur Otrade',
                        status: 'done',
                        color: 'border-green-200 hover:bg-green-50 dark:border-green-800 dark:hover:bg-green-900/20'
                      },
                      {
                        icon: '⭕️',
                        message: 'Aucune relance à faire par le BO',
                        status: 'wontdo',
                        color: 'border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20'
                      },
                      {
                        icon: '❌',
                        message: "N'a pas effectué la mission",
                        status: 'wontdo',
                        color: 'border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20'
                      }
                    ].map((preset, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSubmitFollowup(preset.status, preset.message)}
                        disabled={isSubmitting}
                        className={`flex items-center gap-2 p-2 border-2 rounded-lg transition-all duration-200 text-left ${preset.color} disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <span className="text-lg flex-shrink-0">{preset.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                            {preset.message}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {getStatusLabel(preset.status)}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Séparateur */}
                <div className="relative mb-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                      ou message personnalisé
                    </span>
                  </div>
                </div>

                <form onSubmit={(e) => { e.preventDefault(); handleSubmitFollowup(); }}>
                  <div className="mb-4">
                    <textarea
                      ref={textareaRef}
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Décrivez votre action de relance... (Cmd+Entrée pour soumettre rapidement)"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                      rows={3}
                    />
                  </div>
                  <StatusDropdownButton
                    onSubmit={(status) => handleSubmitFollowup(status)}
                    isSubmitting={isSubmitting}
                    disabled={!newNote.trim()}
                  />
                </form>
              </div>
            ) : (
              <div className="mb-6">
                <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      {currentStatus === 'done' ? (
                        <span className="text-2xl">✅</span>
                      ) : (
                        <span className="text-2xl">❌</span>
                      )}
                    </div>
                    <div>
                      <h4 className="text-md font-medium text-gray-900 dark:text-gray-100">
                        {currentStatus === 'done' ? 'Formulaire terminé' : 'Formulaire abandonné'}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {currentStatus === 'done'
                          ? 'Le promoteur a rempli son formulaire. Aucune relance supplémentaire nécessaire.'
                          : 'Ce formulaire a été marqué comme abandonné. Aucune relance supplémentaire possible.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Affichage des erreurs */}
            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
              </div>
            )}

            {/* Liste des followups */}
            <div>
              <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">
                Historique des relances ({followups.length})
              </h4>

              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : followups.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {followups.map((followup, index) => (
                    <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            Par <strong>{followup.username === 'vous' ? 'vous' : followup.username}</strong>
                          </span>
                          {followup.status && getStatusDisplay(followup.status)}
                        </div>
                        <div className="flex items-center gap-2">
                          <small className="text-gray-500 dark:text-gray-400">#{`${followup.missiondayid}.${followup.promoterid}`}</small>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {followup.username === 'vous' ? 'à l\'instant' : safeFormatUTCDateTime(followup.created_at)}
                          </span>

                          {/* Bouton de suppression - visible seulement pour l'auteur */}
                          {(followup.user_id === currentUser.id || currentUser.isadmin) && (
                            <button
                              onClick={() => handleDeleteFollowup(followup.id)}
                              disabled={deletingFollowupId === followup.id}
                              className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Supprimer cette relance"
                            >
                              {deletingFollowupId === followup.id ? (
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {followup.notes}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm italic">
                  Aucune relance enregistrée pour ce formulaire.
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GIFollowupModal;